import { useMemo, useState } from "react";
import { Card, Input, Select } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NC_SEVERITIES, SECS, type NcDto } from "@audit-plan/shared";
import { download } from "../api/client";
import { useNcs } from "../api/ncs";
import { useSuppliersList } from "../api/audits";
import { useAuth } from "../auth/AuthContext";
import { palette } from "../theme";
import { MonitoringOffPill, Pill, SeverityPill, fmtD } from "../components/pills";
import { PageHead } from "../components/audit/PageHead";
import { NcDrawer } from "../components/nc/NcDrawer";
import { FilterBar, PageActions } from "../components/FilterBar";
import { ResponsiveList } from "../components/ResponsiveList";

export default function NcsPage() {
  const { t } = useTranslation();
  const { me } = useAuth();
  const isSup = me?.role === "supplier";
  const [sp, setSp] = useSearchParams();
  const openId = sp.get("id");
  const [sev, setSev] = useState<string>();
  const [status, setStatus] = useState<string>();
  const [supplierId, setSupplierId] = useState<string>();
  const [section, setSection] = useState<string>();
  // la NC sta sullo stabilimento (AUD-F8.4): filtro sugli stabilimenti delle NC caricate
  const [factoryId, setFactoryId] = useState<string>();
  const [q, setQ] = useState("");
  const suppliers = useSuppliersList();
  const list = useNcs({ status, supplierId, q: q.trim() || undefined });
  const secOf = (n: NcDto) => (n.questionCode ? n.questionCode.split(" ")[0] : undefined);
  const base = useMemo(() => (list.data ?? []).filter((n) => (!section || secOf(n) === section) && (!factoryId || n.factoryId === factoryId)), [list.data, section, factoryId]);
  const factoryOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of list.data ?? []) m.set(n.factoryId, `${n.factoryName}${n.factoryCode ? ` · ${n.factoryCode}` : ""}`);
    return [...m].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [list.data]);
  const rows = useMemo(() => base.filter((n) => !sev || n.severity === sev).sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1)), [base, sev]);
  const setOpen = (id: string | null) => {
    const next = new URLSearchParams(sp);
    if (id) next.set("id", id);
    else next.delete("id");
    setSp(next, { replace: true });
  };

  return (
    <div>
      <PageHead
        title={t("ncs")}
        extra={<PageActions items={[{ key: "xlsx", label: t("exportXlsx"), icon: <DownloadOutlined />, onClick: () => void download("/exports/ncs.xlsx") }]} />}
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(130px,100%),1fr))", gap: 12, marginBottom: 16 }}>
        {NC_SEVERITIES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setSev(sev === s ? undefined : s)}
            style={{ textAlign: "left", cursor: "pointer", padding: "10px 14px", borderRadius: 10, background: palette.card, border: `1px solid ${sev === s ? palette.bad : palette.hair}`, boxShadow: sev === s ? `0 0 0 1px ${palette.bad}` : undefined }}
          >
            <div style={{ fontSize: 24, fontWeight: 800, color: palette.heading }}>{base.filter((n) => n.severity === s).length}</div>
            <div style={{ fontSize: 12, color: palette.muted }}>{t(`sev_${s}`)}</div>
          </button>
        ))}
      </div>
      <FilterBar
        active={[sev, status, supplierId, factoryId, section].filter(Boolean).length}
        onReset={() => { setSev(undefined); setStatus(undefined); setSupplierId(undefined); setFactoryId(undefined); setSection(undefined); setQ(""); }}
        search={<Input.Search allowClear placeholder={t("search")} style={{ width: 260, maxWidth: "100%" }} onSearch={setQ} onChange={(e) => !e.target.value && setQ("")} />}
      >
        <Select allowClear placeholder={t("severity")} style={{ width: 160 }} value={sev} onChange={setSev} options={NC_SEVERITIES.map((s) => ({ value: s, label: t(`sev_${s}`) }))} />
        <Select allowClear placeholder={t("status")} style={{ width: 140 }} value={status} onChange={setStatus} options={[{ value: "open", label: t("open") }, { value: "closed", label: t("closed") }]} />
        {isSup ? null : <Select allowClear showSearch optionFilterProp="label" placeholder={t("supplier")} style={{ width: 220 }} value={supplierId} onChange={setSupplierId} options={(suppliers.data ?? []).map((s) => ({ value: s.id, label: s.name }))} />}
        <Select allowClear showSearch optionFilterProp="label" placeholder={t("nc_factoryFilter")} style={{ width: 240 }} value={factoryId} onChange={setFactoryId} options={factoryOptions} />
        <Select allowClear placeholder={t("section")} style={{ width: 170 }} value={section} onChange={setSection} options={SECS.map((s) => ({ value: s, label: t(`sec_${s}`) }))} />
      </FilterBar>
      <Card styles={{ body: { padding: 0 } }}>
        <ResponsiveList<NcDto>
          rowKey="id"
          loading={list.isLoading}
          dataSource={rows}
          pageSize={10}
          pageSizeKey="ncs"
          emptyText={t("noResults")}
          onRowClick={(n) => setOpen(n.id)}
          card={{
            code: (n) => `${n.code}${n.auditCode ? ` · ${n.auditCode}` : ""}`,
            title: (n) => n.title,
            subtitle: (n) => (isSup || n.factoryName === n.supplierName ? n.factoryName : `${n.factoryName} · ${n.supplierName}`),
            tags: (n) => (
              <>
                <SeverityPill s={n.severity} />
                <Pill tone={n.status === "closed" ? "ok" : "bad"}>{n.status === "closed" ? t("closed") : t("open")}</Pill>
                {n.status === "open" && <MonitoringOffPill off={n.monitoringOff} />}
              </>
            ),
            fields: (n) => [
              [t("question"), n.questionCode ?? "—"],
              [t("openDate"), fmtD(n.createdAt)],
              [t("cap"), n.actionCount ? n.actionCount : "—"],
            ],
          }}
          columns={[
            { title: t("code"), dataIndex: "code", render: (c: string) => <span style={{ fontFamily: "var(--ap-mono)" }}>{c}</span> },
            { title: t("nc_factory"), dataIndex: "factoryName", render: (_: unknown, n) => <div style={{ minWidth: 160 }}>{n.factoryName}{n.factoryCode ? <span style={{ fontFamily: "var(--ap-mono)", opacity: 0.6 }}> {n.factoryCode}</span> : null}{!isSup && n.supplierName !== n.factoryName ? <div style={{ fontSize: 12, color: palette.muted }}>{n.supplierName}</div> : null}</div> },
            { title: t("severity"), render: (_: unknown, n) => <SeverityPill s={n.severity} /> },
            { title: t("title"), render: (_: unknown, n) => <div style={{ maxWidth: 520 }}>{n.title}<div style={{ fontSize: 12, color: palette.muted }}>{(n.description || "").slice(0, 100)}</div></div> },
            { title: t("question"), dataIndex: "questionCode", render: (c: string | null) => c ?? "—" },
            { title: "Audit", dataIndex: "auditCode", render: (c: string | null, n) => (c ? <span style={{ fontFamily: "var(--ap-mono)", fontSize: 12 }}>{c}</span> : <span style={{ fontSize: 12, color: palette.muted }}>{t("reg_badge")}</span>) },
            { title: t("openDate"), dataIndex: "createdAt", render: (d: string) => fmtD(d) },
            { title: t("cap"), dataIndex: "actionCount", align: "right" as const, render: (c?: number) => (c ? c : "—") },
            { title: t("status"), render: (_: unknown, n) => <span style={{ whiteSpace: "nowrap" }}><Pill tone={n.status === "closed" ? "ok" : "bad"}>{n.status === "closed" ? t("closed") : t("open")}</Pill>{n.status === "open" && <MonitoringOffPill off={n.monitoringOff} />}</span> },
          ]}
        />
      </Card>
      <NcDrawer ncId={openId} onClose={() => setOpen(null)} />
    </div>
  );
}
